// DNA CONNECT - Meus Filhos
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, Calendar, School, Heart, Phone, Mail, MapPin,
  Loader2, ChevronRight, GraduationCap, AlertCircle, CreditCard, QrCode, Download, ExternalLink, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

import { formatDatePure } from '@/lib/dateUtils';
import { RematriculaFilhoCta } from '@/pages/DnaConnect/Rematricula2027';

const API_URL = window.location.origin;

export default function DnaConnectFilhos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [filhos, setFilhos] = useState([]);
  const [selectedFilho, setSelectedFilho] = useState(null);

  useEffect(() => {
    loadFilhos();
  }, []);

  const loadFilhos = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/filhos?token=${token}`);
      
      if (!response.ok) throw new Error('Erro ao carregar filhos');
      
      const data = await response.json();
      setFilhos(data);
      if (data.length > 0) {
        setSelectedFilho(data[0]);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // V9.17 · foto do aluno por upload (mesma fonte do painel)
  const [fotoBusy, setFotoBusy] = useState(false);
  const fotoSrc = (f) => {
    const u = f?.photo_url;
    if (!u) return '/default-avatar.png';
    return /^https?:\/\//i.test(u) ? u : `${API_URL}${u}`;
  };
  const enviarFoto = async (e, filho) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error('A foto passa de 8 MB. Escolha uma menor.');
    const token = localStorage.getItem('familyToken');
    const fd = new FormData();
    fd.append('file', file);
    setFotoBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/students/${filho.id}/photo/family?token=${token}`,
        { method: 'POST', body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Falha ao enviar a foto');
      const url = `${j.photo_url}`;
      setSelectedFilho((s) => (s ? { ...s, photo_url: url } : s));
      setFilhos((list) => list.map((x) => (x.id === filho.id ? { ...x, photo_url: url } : x)));
      toast.success('Foto atualizada!');
    } catch (err) { toast.error(err.message); }
    finally { setFotoBusy(false); }
  };

  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return '';
    const match = String(dataNascimento).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const [, y, m, d] = match;
    const hoje = new Date();
    let idade = hoje.getFullYear() - parseInt(y);
    const mDiff = (hoje.getMonth() + 1) - parseInt(m);
    if (mDiff < 0 || (mDiff === 0 && hoje.getDate() < parseInt(d))) idade--;
    return `${idade} anos`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Meus Filhos</h1>
      </div>

      {filhos.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <User className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Nenhum aluno vinculado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de Filhos */}
          <div className="space-y-3">
            {filhos.map((filho) => (
              <Card 
                key={filho.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedFilho?.id === filho.id ? 'ring-2 ring-purple-500' : ''
                }`}
                onClick={() => setSelectedFilho(filho)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={fotoSrc(filho)} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-14 h-14 rounded-xl object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{filho.name}</h3>
                      <p className="text-sm text-gray-500">{filho.class_name}</p>
                      <Badge 
                        variant={filho.situation === 'Ativo' ? 'default' : 'secondary'}
                        className="mt-1"
                      >
                        {filho.situation}
                      </Badge>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <RematriculaFilhoCta studentId={filho.id} />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Detalhes do Filho Selecionado */}
          {selectedFilho && (
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="border-b">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden relative group">
                      <img src={fotoSrc(selectedFilho)} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-20 h-20 rounded-2xl object-cover" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{selectedFilho.name}</CardTitle>
                      <p className="text-gray-500">{selectedFilho.class_name}</p>
                      <input id="dnaconnect-foto-input" type="file" accept="image/*" capture="environment"
                        className="hidden" onChange={(e) => enviarFoto(e, selectedFilho)} data-testid="dnaconnect-foto-input" />
                      <Button size="sm" variant="outline" className="mt-2" disabled={fotoBusy}
                        onClick={() => document.getElementById('dnaconnect-foto-input')?.click()}
                        data-testid="dnaconnect-foto-btn">
                        {fotoBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                        Trocar foto
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-6">
                  <Tabs defaultValue="dados">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="dados">Dados</TabsTrigger>
                      <TabsTrigger value="turma">Turma</TabsTrigger>
                      <TabsTrigger value="saude">Saúde</TabsTrigger>
                      <TabsTrigger value="carteirinha">Carteirinha</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="dados" className="space-y-4 mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InfoItem 
                          icon={Calendar} 
                          label="Data de Nascimento" 
                          value={formatDatePure(selectedFilho.birth_date)}
                        />
                        <InfoItem 
                          icon={User} 
                          label="Idade" 
                          value={calcularIdade(selectedFilho.birth_date)}
                        />
                        <InfoItem 
                          icon={School} 
                          label="Matrícula" 
                          value={selectedFilho.registration_number || '-'}
                        />
                        <InfoItem 
                          icon={GraduationCap} 
                          label="RA" 
                          value={selectedFilho.ra || '-'}
                        />
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="turma" className="space-y-4 mt-4">
                      {selectedFilho.turma ? (
                        <div className="space-y-4">
                          <InfoItem 
                            icon={School} 
                            label="Turma" 
                            value={selectedFilho.turma.name}
                          />
                          <InfoItem 
                            icon={Calendar} 
                            label="Período" 
                            value={selectedFilho.turma.shift}
                          />
                          <InfoItem 
                            icon={GraduationCap} 
                            label="Nível de Ensino" 
                            value={selectedFilho.turma.education_level}
                          />
                          
                          {selectedFilho.turma.google_classroom_link && (
                            <a 
                              href={selectedFilho.turma.google_classroom_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block"
                            >
                              <Button variant="outline" className="gap-2">
                                <School className="w-4 h-4" />
                                Acessar Google Classroom
                              </Button>
                            </a>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-500">Informações da turma não disponíveis</p>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="saude" className="space-y-4 mt-4">
                      <div className="space-y-4">
                        <InfoItem 
                          icon={Heart} 
                          label="Tipo Sanguíneo" 
                          value={selectedFilho.blood_type || 'Não informado'}
                        />
                        
                        <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                          <div className="flex items-center gap-2 text-red-700 mb-2">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">Alergias</span>
                          </div>
                          <p className="text-sm text-red-600">
                            {selectedFilho.allergies || 'Nenhuma alergia informada'}
                          </p>
                        </div>
                        
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                          <div className="flex items-center gap-2 text-amber-700 mb-2">
                            <Heart className="w-5 h-5" />
                            <span className="font-medium">Doenças / Condições</span>
                          </div>
                          <p className="text-sm text-amber-600">
                            {selectedFilho.diseases || 'Nenhuma condição informada'}
                          </p>
                        </div>
                        
                        {selectedFilho.first_aid_notes && (
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <div className="flex items-center gap-2 text-blue-700 mb-2">
                              <AlertCircle className="w-5 h-5" />
                              <span className="font-medium">Observações de Primeiros Socorros</span>
                            </div>
                            <p className="text-sm text-blue-600">{selectedFilho.first_aid_notes}</p>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="carteirinha" className="mt-4">
                      {/* Preview da Carteirinha + Botão para abrir página dedicada */}
                      <div className="space-y-4">
                        {/* Preview Simples */}
                        <div className="bg-gradient-to-r from-purple-50 to-green-50 rounded-xl p-6 text-center">
                          <div className="w-20 h-20 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <CreditCard className="w-10 h-10 text-white" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-800 mb-2">Carteirinha Digital</h3>
                          <p className="text-gray-600 mb-4">
                            Acesse a carteirinha digital de <strong>{selectedFilho.name?.split(' ')[0]}</strong> para usar em cinemas, teatros e eventos.
                          </p>
                          <Button 
                            onClick={() => navigate(`/familia/carteirinha/${selectedFilho.id}`)}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 text-lg"
                          >
                            <ExternalLink className="w-5 h-5 mr-2" />
                            Abrir Carteirinha
                          </Button>
                        </div>
                        
                        {/* Mini Preview */}
                        <div className="relative p-0.5 bg-gradient-to-br from-purple-400 via-pink-300 to-green-400 rounded-2xl max-w-sm mx-auto">
                          <div className="bg-white rounded-xl p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-xs">DNA</span>
                              </div>
                              <div>
                                <p className="font-bold text-gray-800 text-sm">NOVO DNA</p>
                                <p className="text-[10px] text-gray-500">INSTITUTO DE APRENDIZAGEM</p>
                              </div>
                              <div className="ml-auto bg-purple-600 text-white px-2 py-1 rounded text-[9px] font-bold">
                                DIGITAL
                              </div>
                            </div>
                            <div className="flex gap-3">
                              <div className="w-16 h-20 rounded overflow-hidden border-2 border-pink-400 bg-gray-100 flex-shrink-0">
                                <img src={fotoSrc(selectedFilho)} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1">
                                <p className="font-bold text-gray-800 text-sm leading-tight">{selectedFilho.name?.split(' ').slice(0, 2).join(' ')}</p>
                                <p className="text-amber-500 font-bold">RM: {selectedFilho.registration_number}</p>
                                <p className="text-green-500 font-bold text-lg">{new Date().getFullYear()}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-center text-gray-500 text-sm">
                          Clique em "Abrir Carteirinha" para ver a versão completa
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>

                  {/* Ações Rápidas */}
                  <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t">
                    <Link to="/familia/pedagogico">
                      <Button variant="outline" size="sm">
                        <GraduationCap className="w-4 h-4 mr-2" />
                        Ver Boletim
                      </Button>
                    </Link>
                    <Link to="/familia/dnago">
                      <Button variant="outline" size="sm">
                        <User className="w-4 h-4 mr-2" />
                        DNA GO
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-purple-600" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
}
